"use client";

import { motion } from "motion/react";
import { Pencil } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import Image from "next/image";
import { SuggestionPills } from "./suggestion-pills";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { DEFAULT_EMPTY_STATE_WELCOME } from "@/lib/empty-state-welcome";

interface EmptyStateProps {
  children: ReactNode;
}

export const DEFAULT_WELCOME_MESSAGE = DEFAULT_EMPTY_STATE_WELCOME;

export function EmptyState({ children }: EmptyStateProps) {
  const [welcomeMessage, setWelcomeMessage] = useState(DEFAULT_WELCOME_MESSAGE);
  const [typedMessage, setTypedMessage] = useState("");
  const [typingComplete, setTypingComplete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [draftMessage, setDraftMessage] = useState(DEFAULT_WELCOME_MESSAGE);
  const [isSaving, setIsSaving] = useState(false);
  const [editorStatus, setEditorStatus] = useState<string | null>(null);

  const shouldShowCursor = useMemo(() => !typingComplete, [typingComplete]);

  useEffect(() => {
    const loadWelcome = async () => {
      try {
        const response = await fetch("/api/empty-state-welcome", {
          cache: "no-store",
        });
        const payload = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        if (response.ok && typeof payload.message === "string") {
          setWelcomeMessage(payload.message);
        }
      } catch {
        // Keep the default copy if the API is unavailable.
      }
    };

    const loadAdminSession = async () => {
      try {
        const response = await fetch("/api/admin/session", {
          cache: "no-store",
        });
        const payload = (await response.json().catch(() => ({}))) as {
          isAdmin?: boolean;
        };
        setIsAdmin(Boolean(response.ok && payload.isAdmin));
      } catch {
        setIsAdmin(false);
      }
    };

    void loadWelcome();
    void loadAdminSession();
  }, []);

  useEffect(() => {
    let timeoutId: number | undefined;
    let index = 0;

    setTypedMessage("");
    setTypingComplete(false);

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) {
      setTypedMessage(welcomeMessage);
      setTypingComplete(true);
      return;
    }

    const typeNext = () => {
      index += 1;
      setTypedMessage(welcomeMessage.slice(0, index));

      if (index >= welcomeMessage.length) {
        setTypingComplete(true);
        return;
      }

      const nextChar = welcomeMessage[index];
      let delay = 16;

      if (nextChar === "\n") delay = 80;
      else if (nextChar === "." || nextChar === "!" || nextChar === "?") delay = 48;
      else if (nextChar === ",") delay = 28;

      timeoutId = window.setTimeout(typeNext, delay);
    };

    timeoutId = window.setTimeout(typeNext, 100);

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [welcomeMessage]);

  const openEditor = () => {
    setDraftMessage(welcomeMessage);
    setEditorStatus(null);
    setIsEditorOpen(true);
  };

  const saveWelcome = async () => {
    setIsSaving(true);
    setEditorStatus(null);
    try {
      const response = await fetch("/api/empty-state-welcome", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: draftMessage }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!response.ok || typeof payload.message !== "string") {
        setEditorStatus(payload.error ?? "Failed to save welcome text.");
        return;
      }
      setWelcomeMessage(payload.message);
      setIsEditorOpen(false);
    } catch {
      setEditorStatus("Failed to save welcome text.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-1 items-center justify-center w-full h-full relative overflow-hidden bg-gradient-to-b from-white via-[var(--mindup-off-white)] to-white dark:from-[var(--mindup-soft-black)] dark:via-[#333] dark:to-[var(--mindup-soft-black)]">
      <motion.div
        className="absolute top-16 left-[5%] w-72 h-72 bg-gradient-to-br from-[var(--mindup-pink-light)]/60 to-[var(--mindup-pink)]/20 blob-shape pointer-events-none blur-2xl"
        animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.7, 0.5] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-16 right-[10%] w-80 h-80 bg-gradient-to-br from-[var(--mindup-mint)]/50 to-[var(--mindup-green)]/20 blob-shape-2 pointer-events-none blur-2xl"
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      <motion.div
        className="absolute top-[40%] right-[30%] w-48 h-48 bg-gradient-to-br from-[var(--mindup-blue-light)]/40 to-[var(--mindup-blue)]/15 blob-shape-3 pointer-events-none blur-2xl"
        animate={{ scale: [1, 1.08, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="absolute bottom-0 right-0 w-[min(75vw,840px)] h-[min(75vw,840px)] pointer-events-none opacity-[0.18] dark:opacity-[0.12]">
        <Image
          src="/BRAIN_recolored.png"
          alt=""
          fill
          className="object-contain object-right-bottom"
          aria-hidden
        />
      </div>

      <div className="max-w-3xl mx-auto w-full px-6 space-y-10 relative z-10">
        <motion.div
          className="text-left space-y-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="max-w-2xl mx-auto text-left space-y-5">
            <div className="relative">
              <p className="text-lg sm:text-xl text-[#555] dark:text-gray-300 leading-relaxed whitespace-pre-line pr-10">
                {typedMessage}
                {shouldShowCursor && (
                  <span className="inline-block ml-0.5 animate-pulse text-[var(--mindup-pink)]">
                    |
                  </span>
                )}
              </p>
              {isAdmin && (
                <button
                  type="button"
                  onClick={openEditor}
                  className="absolute -right-1 -top-1 h-8 w-8 rounded-md inline-flex items-center justify-center text-[#555]/70 hover:text-[#555] hover:bg-white/80"
                  aria-label="Edit welcome text"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
            <video
              src="/videos/Mindup_intro_web.mp4"
              controls
              className="w-full rounded-xl border border-gray-200 dark:border-white/10 shadow-lg"
              aria-label="MindUP intro video"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          {children}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <SuggestionPills />
        </motion.div>
      </div>

      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit welcome text</DialogTitle>
            <DialogDescription>
              This copy appears above the intro video on the empty chat screen.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={draftMessage}
            onChange={(event) => setDraftMessage(event.target.value)}
            className="min-h-32"
          />
          {editorStatus && <p className="text-xs text-red-600">{editorStatus}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveWelcome} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
